<template>
  <!-- eslint-disable max-len-->
  <div class="quick-share">
    <div class="tabs is-medium is-boxed">
      <ul>
        <li
          :class="active_tab == 'share' ? 'is-active' : ''"
          @click="toggleTab('share')"
        >
          <a>
            <span class="icon is-small">
              <i class="fa fa-share" />
            </span>
            <span>{{ $t('quick-share.share') }} ( {{ available_count }} )</span>
          </a>
        </li>
        <li
          :class="active_tab == 'sharing' ? 'is-active' : ''"
          @click="toggleTab('sharing')"
        >
          <a>
            <span class="icon is-small">
              <i class="fa fa-share-alt" />
            </span>
            <span>{{ $t('quick-share.sharing') }} ( {{ sharing_list_count }} )</span>
          </a>
        </li>
        <li style="width: 100%">
          <button
            v-if="available_count"
            type="button"
            class="button is-medium is-primary is-pulled-right"
            @click="addMultipleToShareList"
          >
            <span class="icon"><i class="fa fa-share" /></span>
            <span>{{ $t('quick-share.add-multiple-recipients') }}</span>
          </button>
        </li>
      </ul>
    </div>

    <div v-show="active_tab == 'share'" class="content">
      <div class="columns">
        <div class="column is-12">
          <div class="level">
            <div class="level-left">
              <h2 class="title is-3" v-html="$tc('quick-share.available-shares-left', available_count, {count: available_count})"/>
            </div>
            <div class="level-right">
              <button
                v-if="available_count"
                type="button"
                class="button is-medium is-primary"
                @click="addToShareList"
              >
                <span class="icon">
                  <i class="fa fa-share" />
                </span>
                <span>{{ $t('quick-share.add-recipient') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <table
        v-if="share_list_count"
        class="table is-striped is-hoverable is-fullwidth"
      >
        <thead>
          <tr>
            <th>#</th>
            <th v-html="$t('quick-share.available-till')" />
            <th v-text="$t('quick-share.recipient')" />
            <th
              class="is-centered"
              v-text="$t('quick-share.share-with-ticket')"
            />
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="(course, key) in share_list" :key="key">
            <td v-text="key + 1" />
            <td v-text="course.available_till" />
            <td>
              <input
                v-if="course.share_type == 'email'"
                v-model="course.share_to"
                :placeholder="$t('quick-share.share-to-email-address')"
                :class="course.has_errors ? 'is-danger' : ''"
                class="input is-fullwidth"
                type="email"
              />
            </td>
            <td class="is-centered">
              <input
                v-model="course.share_type"
                :true-value="'ticket'"
                :false-value="'email'"
                type="checkbox"
                class="checkbox"
              />
            </td>
            <td class="is-right">
              <button
                type="button"
                class="button is-danger"
                @click="course.share = false"
              >
                <i class="fa fa-remove" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="share_list_count" class="field">
        <div v-if="share_list_with_ticket_count" class="notification is-info" style="z-index: 0;">
          {{ $t('quick-share.share_with_ticket_notice_1') }}
          <b>{{ $t('quick-share.share_with_ticket_notice_2') }}</b>
        </div>
        <p class="control is-right">
          <button
            :disable="processing"
            type="button"
            class="button is-primary is-medium is-outlined"
            @click.prevent="share()"
          >
            <i v-if="processing" class="material-icons">
              sync
            </i>
            {{ $t('quick-share.share-button') }}
          </button>
        </p>
      </div>
    </div>

    <div v-show="active_tab == 'sharing'" class="content">
      <Sharing :sharing="sharing_list" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { find, filter, first, each, concat } from 'lodash';
import Validate from 'helpers/Validators';
import Sharing from './Sharing';

export default {
  name: 'QuickShare',

  components: {
    Sharing
  },

  props: {
    available_for_sharing_items: { required: true, type: Array },
    sharing_items: { required: true, type: Array }
  },

  data() {
    return {
      processing: false,
      active_tab: 'share',
      items: concat(this.sharing_items, this.available_for_sharing_items),
      sharing_list: this.sharing_items
    };
  },

  computed: {
    sharing_list_count() {
      return this.sharing_list.length;
    },

    share_list() {
      return filter(
        this.available_for_sharing_items,
        item => item.share && item.status === 'created',
      );
    },

    share_list_count() {
      return this.share_list.length;
    },

    share_list_with_ticket_count() {
      return this.share_list.filter(
        course => course.share_type === 'ticket',
      ).length;
    },

    available() {
      return filter(this.available_for_sharing_items, item => item.share === false);
    },

    available_count() {
      return this.available.length;
    }
  },

  methods: {
    addToShareList($e, email = null) {
      const course = first(this.available);
      course.share = true;
      if (email) {
        course.share_to = email;
      }
    },
    addMultipleToShareList() {
      Swal.fire({
        type: 'info',
        html: '<b>Copy and paste all emails (separated by semicolon or comma) into the input below.</b>',
        input: 'textarea',
        inputPlaceholder: 'Paste emails here',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        reverseButtons: false,
        inputValidator: (value) => {
          if (!value) {
            return 'You need to write something!';
          }
          const emails = this.getEmailsFromString(value);
          if (emails.length > this.available_count) {
            return 'You have only ' + this.available_count + ' courses.';
          }
          if (emails.length > 50) {
            return 'You cannot share more than 50 courses at once!';
          }
          let has_invalid_emails = false;
          emails.forEach(email => {
            const is_email_valid = /\S+@\S+\.\S+/.test(email);
            if (!is_email_valid) {
              has_invalid_emails = true;
            }
          });
          if (emails.length === 0 || has_invalid_emails) {
            return 'You have incorrect emails';
          }
          if (emails.some((val, i) => emails.indexOf(val) !== i)) {
            return 'You have duplicated emails';
          }
        }
      }).then(result => {
        const emails = this.getEmailsFromString(result.value);
        emails.forEach(email => {
          this.addToShareList(null, email);
        });
      });
    },
    getEmailsFromString(text) {
      return text.replace(/\s/g, '').split(/,|;/).filter(n => n);
    },
    share() {
      this.processing = true;

      each(this.share_list, course => {
        course.has_errors = false;
        if (
          course.share_type === 'email' &&
          find(this.sharing_list, ['share_to', course.share_to])
        ) {
          Swal.fire({
            title: 'Confirm?',
            html: `This course is already shared to <em><b>${course.share_to}</b></em>. Share another course instance?`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes!'
          }).then(result => {
            if (result.value) {
              this.shareOne(course);
            }
          });
        } else {
          this.shareOne(course);
        }
      });

      this.processing = false;
    },

    shareOne(course) {
      if (Validate.email(course.share_to)) {
        axios
          .post(`/quick-share/${course.id}`, {
            email: course.share_to,
            share_type: course.share_type
          })
          .then(({ data }) => {
            course.status = 'shared';
            course.share_to = data.shared_to;
            flash(
              this.$t('quick-share.share-success', { email: course.share_to }),
            );
          })
          .catch((response) => {
            if (response?.response?.data?.message) {
              error(
                response.response.data.message,
              );
            } else {
              error(
                this.$t('quick-share.share-error', { email: course.share_to }),
              );
            }
          });
      } else {
        course.has_errors = true;
      }
    },

    toggleTab(name) {
      this.active_tab = name;
    }
  }
};
</script>
